import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { PeopelsLogo } from "@/components/brand/PeopelsLogo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import {
  listOnboardingCategories,
  saveHelperSkills,
  saveHelperAvailability,
  suggestPricing,
  savePricing,
} from "@/lib/helper-onboarding.functions";
import { trackOnboard } from "@/lib/onboard-track";
import {
  Loader2, CheckCircle2, ChevronRight, ChevronLeft, Search, Clock, Wand2, Sparkles, PartyPopper,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/helper/onboard/wizard")({
  head: () => ({ meta: [{ title: "Set up your Helper profile — Peopels" }, { name: "robots", content: "noindex" }] }),
  component: WizardPage,
});

type Category = { id: string; slug: string; name: string; icon: string | null };
type Day = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";
type Slot = "morning" | "afternoon" | "evening";
const DAYS: { key: Day; label: string }[] = [
  { key: "mon", label: "Mon" }, { key: "tue", label: "Tue" }, { key: "wed", label: "Wed" },
  { key: "thu", label: "Thu" }, { key: "fri", label: "Fri" }, { key: "sat", label: "Sat" }, { key: "sun", label: "Sun" },
];
const SLOTS: { key: Slot; label: string; hint: string }[] = [
  { key: "morning", label: "Morning", hint: "8am–12pm" },
  { key: "afternoon", label: "Afternoon", hint: "12pm–5pm" },
  { key: "evening", label: "Evening", hint: "5pm–9pm" },
];

const STEPS = ["services", "availability", "pricing"] as const;
type Step = (typeof STEPS)[number];

function WizardPage() {
  const navigate = useNavigate();
  const fetchCats = useServerFn(listOnboardingCategories);
  const saveCats = useServerFn(saveHelperSkills);
  const saveAvail = useServerFn(saveHelperAvailability);
  const aiSuggest = useServerFn(suggestPricing);
  const savePrice = useServerFn(savePricing);

  const [step, setStep] = useState<Step>("services");
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [cats, setCats] = useState<Category[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [tz, setTz] = useState<string>("Europe/London");
  const [schedule, setSchedule] = useState<Record<Day, Slot[]>>({
    mon: [], tue: [], wed: [], thu: [], fri: [], sat: [], sun: [],
  });
  const [tier, setTier] = useState<"starter" | "standard" | "premium">("standard");
  const [years, setYears] = useState<number>(0);
  const [rate, setRate] = useState<number>(30);
  const [suggestion, setSuggestion] = useState<{ low: number; mid: number; high: number; recommended: number; rationale: string } | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.auth.getUser();
      if (!data.user) { navigate({ to: "/auth", search: { redirect: "/helper/onboard/wizard" } }); return; }
      setAuthed(true);
      try { setTz(Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/London"); } catch { /* */ }
      const list = await fetchCats() as Category[];
      setCats(list);
      void trackOnboard("services", "view");
    })();
  }, [fetchCats, navigate]);

  const filteredCats = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return cats;
    return cats.filter((c) => c.name.toLowerCase().includes(q) || c.slug.includes(q));
  }, [cats, search]);

  const selectedCats = useMemo(() => cats.filter((c) => selected.has(c.id)), [cats, selected]);

  // Smart default presets
  function applyWeekdayPreset() {
    setSchedule({ mon: ["morning", "afternoon"], tue: ["morning", "afternoon"], wed: ["morning", "afternoon"], thu: ["morning", "afternoon"], fri: ["morning", "afternoon"], sat: [], sun: [] });
  }
  function applyEveningsWeekendsPreset() {
    setSchedule({ mon: ["evening"], tue: ["evening"], wed: ["evening"], thu: ["evening"], fri: ["evening"], sat: ["morning", "afternoon"], sun: ["morning", "afternoon"] });
  }
  function applyFullTimePreset() {
    const all: Slot[] = ["morning", "afternoon", "evening"];
    setSchedule({ mon: all, tue: all, wed: all, thu: all, fri: all, sat: all, sun: all });
  }
  function toggleSlot(d: Day, s: Slot) {
    setSchedule((prev) => {
      const has = prev[d].includes(s);
      return { ...prev, [d]: has ? prev[d].filter((x) => x !== s) : [...prev[d], s] };
    });
  }

  async function next() {
    setBusy(true);
    try {
      if (step === "services") {
        if (selected.size === 0) { toast.error("Pick at least one service."); return; }
        await saveCats({ data: { categoryIds: Array.from(selected) } });
        await trackOnboard("services", "complete", { props: { count: selected.size } });
        setStep("availability");
        await trackOnboard("availability", "view");
      } else if (step === "availability") {
        const hasAny = Object.values(schedule).some((arr) => arr.length > 0);
        if (!hasAny) { toast.error("Pick at least one time slot."); return; }
        await saveAvail({ data: { timezone: tz, schedule } });
        await trackOnboard("availability", "complete", { props: { tz } });
        // Pre-fetch AI suggestion before showing pricing
        try {
          const slugs = selectedCats.map((c) => c.slug);
          const s = await aiSuggest({ data: { categorySlugs: slugs, yearsExperience: years, tier } });
          setSuggestion(s);
          setRate(s.recommended);
        } catch { /* */ }
        setStep("pricing");
        await trackOnboard("pricing", "view");
      } else {
        await savePrice({ data: { hourlyRate: rate, tier, yearsExperience: years } });
        await trackOnboard("pricing", "complete", { props: { rate, tier } });
        toast.success("Onboarding complete!");
        navigate({ to: "/helper/onboard" });
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Something went wrong";
      toast.error(msg);
      await trackOnboard(step, "error", { errorMessage: msg });
    } finally { setBusy(false); }
  }

  async function refreshSuggestion() {
    try {
      const slugs = selectedCats.map((c) => c.slug);
      const s = await aiSuggest({ data: { categorySlugs: slugs, yearsExperience: years, tier } });
      setSuggestion(s);
      setRate(s.recommended);
    } catch { toast.error("Couldn't fetch suggestion"); }
  }

  if (authed === null) {
    return (
      <div className="flex min-h-screen flex-col bg-surface">
        <Header />
        <main className="grid flex-1 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></main>
        <Footer />
      </div>
    );
  }

  const stepIdx = STEPS.indexOf(step);
  const percent = Math.round(((stepIdx) / STEPS.length) * 100);

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <Header />
      <main id="main-content" className="mx-auto w-full max-w-3xl flex-1 px-4 py-6 sm:px-6 sm:py-10 lg:px-8">
        <PeopelsLogo variant="primary" height={48} className="mb-6" />
        <div className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">Step {stepIdx + 1} of {STEPS.length}</p>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted">
            <div className="h-full rounded-full bg-gradient-to-r from-primary to-emerald-500 transition-all duration-500" style={{ width: `${percent + 100 / STEPS.length}%` }} />
          </div>
        </div>

        {step === "services" && (
          <section>
            <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">What services do you offer?</h1>
            <p className="mt-1 text-sm text-muted-foreground">Pick all that apply — you can change these later.</p>
            <div className="relative mt-4">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search services (e.g. handyman, TV mounting)…" className="pl-9" />
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
              {filteredCats.map((c) => {
                const on = selected.has(c.id);
                return (
                  <button
                    type="button"
                    key={c.id}
                    onClick={() => setSelected((p) => { const n = new Set(p); on ? n.delete(c.id) : n.add(c.id); return n; })}
                    className={`grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 rounded-xl border p-3 text-left transition-colors ${on ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/40"}`}
                  >
                    <span className="text-lg" aria-hidden>{c.icon ? "•" : "•"}</span>
                    <span className="truncate text-sm font-medium">{c.name}</span>
                    {on && <CheckCircle2 className="h-4 w-4 text-primary" />}
                  </button>
                );
              })}
            </div>
            {selectedCats.length > 0 && (
              <div className="mt-5 rounded-xl bg-accent/40 p-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-primary">You'll offer</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {selectedCats.map((c) => <Badge key={c.id} variant="secondary">{c.name}</Badge>)}
                </div>
              </div>
            )}
          </section>
        )}

        {step === "availability" && (
          <section>
            <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">When are you available?</h1>
            <p className="mt-1 text-sm text-muted-foreground">Times shown in <strong>{tz}</strong>. Pick a quick preset or tap slots.</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button type="button" size="sm" variant="outline" onClick={applyWeekdayPreset}><Clock className="mr-1 h-3.5 w-3.5" /> Weekdays 9–5</Button>
              <Button type="button" size="sm" variant="outline" onClick={applyEveningsWeekendsPreset}><Clock className="mr-1 h-3.5 w-3.5" /> Evenings &amp; weekends</Button>
              <Button type="button" size="sm" variant="outline" onClick={applyFullTimePreset}><Clock className="mr-1 h-3.5 w-3.5" /> Anytime</Button>
            </div>
            <div className="mt-5 overflow-hidden rounded-2xl border border-border bg-card">
              <div className="grid grid-cols-[80px_repeat(3,minmax(0,1fr))] gap-px bg-border text-xs">
                <div className="bg-card p-2 font-semibold" />
                {SLOTS.map((s) => (
                  <div key={s.key} className="bg-card p-2 text-center font-semibold">
                    <div>{s.label}</div>
                    <div className="text-[10px] font-normal text-muted-foreground">{s.hint}</div>
                  </div>
                ))}
                {DAYS.map((d) => (
                  <Row key={d.key} day={d} schedule={schedule} toggleSlot={toggleSlot} />
                ))}
              </div>
            </div>
          </section>
        )}

        {step === "pricing" && (
          <section>
            <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Set your hourly rate</h1>
            <p className="mt-1 text-sm text-muted-foreground">AI-guided pricing based on your services, tier, and experience.</p>

            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              {(["starter", "standard", "premium"] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTier(t)}
                  className={`rounded-xl border p-3 text-left transition-colors ${tier === t ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/40"}`}
                >
                  <p className="text-sm font-semibold capitalize">{t}</p>
                  <p className="text-xs text-muted-foreground">
                    {t === "starter" ? "Just getting started" : t === "standard" ? "Experienced & reliable" : "Top-tier with credentials"}
                  </p>
                </button>
              ))}
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Years experience</Label>
                <Input
                  type="number" min={0} max={60}
                  value={years}
                  onChange={(e) => setYears(Math.max(0, Math.min(60, Number(e.target.value || 0))))}
                />
              </div>
              <div className="flex items-end">
                <Button type="button" variant="outline" className="w-full" onClick={refreshSuggestion}>
                  <Wand2 className="mr-1 h-3.5 w-3.5" /> Re-run AI suggestion
                </Button>
              </div>
            </div>

            {suggestion && (
              <div className="mt-5 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/5 to-emerald-500/5 p-4">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-primary" />
                  <p className="text-sm font-semibold">Recommended: £{suggestion.recommended}/hr</p>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">Range £{suggestion.low}–£{suggestion.high} · {suggestion.rationale}</p>
              </div>
            )}

            <div className="mt-5">
              <Label className="text-xs">Your rate: <span className="font-display text-lg font-bold">£{rate}</span>/hr</Label>
              <Slider value={[rate]} min={10} max={150} step={1} onValueChange={(v) => setRate(v[0])} className="mt-2" />
              <p className="mt-1 text-xs text-muted-foreground">You can adjust your rate anytime from your dashboard.</p>
            </div>
          </section>
        )}

        {/* Sticky footer nav */}
        <div className="sticky bottom-0 -mx-4 mt-8 border-t border-border bg-background/80 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6">
          <div className="flex items-center justify-between gap-3">
            {stepIdx > 0 ? (
              <Button type="button" variant="ghost" onClick={() => setStep(STEPS[stepIdx - 1])}>
                <ChevronLeft className="mr-1 h-4 w-4" /> Back
              </Button>
            ) : <Link to="/helper/onboard" className="text-sm text-muted-foreground hover:underline">Skip for now</Link>}
            <Button type="button" onClick={next} disabled={busy} className="rounded-full">
              {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : step === "pricing" ? <PartyPopper className="mr-1 h-4 w-4" /> : null}
              {step === "pricing" ? "Finish setup" : "Continue"}
              {step !== "pricing" && <ChevronRight className="ml-1 h-4 w-4" />}
            </Button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Row({ day, schedule, toggleSlot }: {
  day: { key: Day; label: string };
  schedule: Record<Day, Slot[]>;
  toggleSlot: (d: Day, s: Slot) => void;
}) {
  return (
    <>
      <div className="bg-card p-3 text-sm font-semibold">{day.label}</div>
      {SLOTS.map((s) => {
        const on = schedule[day.key].includes(s.key);
        return (
          <button
            key={s.key}
            type="button"
            onClick={() => toggleSlot(day.key, s.key)}
            className={`min-h-[44px] bg-card text-xs font-medium transition-colors ${on ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-accent"}`}
            aria-pressed={on}
          >
            {on ? "✓" : "—"}
          </button>
        );
      })}
    </>
  );
}
